import React from "react";

// Shared identity primitives for the approval popup + research card so both
// surfaces render the contact the same way: same letter, same color from the
// same hash, same LinkedIn brand mark.

const avatarLetterFor = (s) =>
  (String(s || "?").trim().charAt(0) || "?").toUpperCase();

// Deterministic 1..6 bucket so the same contact always gets the same color.
const avatarClassFor = (s) => {
  const str = String(s || "");
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) | 0;
  return `c${((h % 6) + 6) % 6 + 1}`;
};

// Normalize whatever shape a LinkedIn URL is stored in (full URL,
// "linkedin.com/in/foo", or bare username) into a clickable absolute URL.
export const normalizeLinkedInUrl = (raw) => {
  if (!raw || typeof raw !== "string") return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (trimmed.includes("linkedin.com")) return `https://${trimmed}`;
  return `https://www.linkedin.com/in/${trimmed}`;
};

export const LetterAvatar = ({ seed, size = "md", className = "" }) => (
  <div
    className={`drApprovalAvatar drApprovalAvatar--${avatarClassFor(seed)} drApprovalAvatar--${size} ${className}`}
    aria-hidden="true"
  >
    {avatarLetterFor(seed)}
  </div>
);

// Brand-standard LinkedIn mark: solid #0a66c2 rounded square with white "in".
// Inline SVG so we don't have to mutate the shared Assets/svg/linkedin.svg
// (used in 7+ other places). Self-contained, ~280 bytes.
export const LinkedInBrandIcon = ({ size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <rect width="24" height="24" rx="4" fill="#0a66c2" />
    <path
      d="M6.94 8.5a1.94 1.94 0 1 1 0-3.88 1.94 1.94 0 0 1 0 3.88zM5 10h3.88v9.5H5V10zm5.5 0h3.7v1.3h.05c.52-.93 1.79-1.93 3.68-1.93 3.94 0 4.67 2.6 4.67 5.97v5.16h-3.88v-4.58c0-1.09-.02-2.5-1.52-2.5-1.52 0-1.75 1.19-1.75 2.42v4.66H10.5V10z"
      fill="#fff"
    />
  </svg>
);

export const LinkedInBadge = ({ url, size = 16, className = "" }) => {
  const normalized = normalizeLinkedInUrl(url);
  if (!normalized) return null;
  return (
    <a
      className={`drLinkedInBrandLink ${className}`}
      href={normalized}
      target="_blank"
      rel="noopener noreferrer"
      title="Open LinkedIn profile"
      onClick={e => e.stopPropagation()}
    >
      <LinkedInBrandIcon size={size} />
    </a>
  );
};
